import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface CrawlStatus {
  domain: string;
  totalUrls: number;
  pagesCrawled: number;
  pagesFailed: number;
  pagesInQueue: number;
  pagesAnalyzed?: number;
  isComplete: boolean;
  progress: number; // percentage
  phase?: 'crawling' | 'analyzing' | 'complete' | 'idle';
  estimatedTimeRemaining?: number; // seconds (optional)
}

export interface StartCrawlOptions {
  limit?: number;
  priority?: 'low' | 'medium' | 'high';
  frequency?: 'daily' | 'weekly' | 'monthly' | 'manual';
  enableBrowserRendering?: boolean;
}

export interface StartCrawlResponse {
  success: boolean;
  urlsAdded: number;
  tasksCreated: number;
  message?: string;
}

export type ProcessingStatus = 'no_data' | 'not_started' | 'partial' | 'complete';

export interface LinksBackfillStatus {
  status: ProcessingStatus;
  totalPages: number;
  pagesWithLinks: number;
  lastFetchedAt: string | null;
}

export interface LinksClassifyStatus {
  status: ProcessingStatus;
  pagesWithLinks: number;
  pagesWithClassifiedLinks: number;
  lastClassifiedAt: string | null;
}

export interface TopicsMapStatus {
  status: ProcessingStatus;
  mappingCount: number;
  lastUpdatedAt: string | null;
}

export interface TopicsAggregateStatus {
  status: ProcessingStatus;
  aggregateCount: number;
  pagesWithTopicSignals: number;
  lastUpdatedAt: string | null;
}

export interface CrawlEstimate {
  sitemapUrl: string;
  totalPagesInSitemap: number;
  filteredPages: number;
  limit: number;
  estimatedCostPerPage: number;
  estimatedTotalCost: number;
}

export interface FailedPage {
  url: string;
  errorMessage: string;
  taskId: string;
  createdAt: string;
  completedAt: string | null;
}

export interface DomainProcessingStatus {
  domain: string;
  linksBackfill: LinksBackfillStatus;
  linksClassify: LinksClassifyStatus;
  topicsMap: TopicsMapStatus;
  topicsAggregate: TopicsAggregateStatus;
}

@Injectable({
  providedIn: 'root'
})
export class CrawlService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  /**
   * Get list of all domains
   */
  getDomains(): Observable<{ success: boolean; data: string[] }> {
    return this.http.get<{ success: boolean; data: string[] }>(`${this.API_URL}/urls/domains`);
  }

  /**
   * Start a crawl for a domain
   */
  startCrawl(domain: string, options: StartCrawlOptions = {}): Observable<StartCrawlResponse> {
    return this.http.post<StartCrawlResponse>(`${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl/start`, {
      limit: options.limit || 750,
      priority: options.priority || 'high',
      frequency: options.frequency || 'manual',
      enableBrowserRendering: options.enableBrowserRendering || false
    });
  }

  /**
   * Estimate crawl cost for a domain (parses sitemap, zero API cost)
   */
  estimateCrawl(domain: string, limit?: number): Observable<{ success: boolean; data: CrawlEstimate }> {
    const params = limit ? `?limit=${limit}` : '';
    return this.http.get<{ success: boolean; data: CrawlEstimate }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl/estimate${params}`
    );
  }

  /**
   * Get failed crawl pages for a domain
   */
  getFailedPages(domain: string): Observable<FailedPage[]> {
    return this.http.get<{ success: boolean; data: FailedPage[] }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl/failed`
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Failed to get failed pages');
      })
    );
  }

  /**
   * Get crawl status for a domain
   */
  getCrawlStatus(domain: string): Observable<CrawlStatus> {
    return this.http.get<{ success: boolean; data: CrawlStatus }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl/status`
    ).pipe(
      // Extract data from response
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        } else {
          throw new Error('Failed to get crawl status');
        }
      })
    );
  }

  /**
   * Get processing status for a domain (links/topics workflows)
   */
  getProcessingStatus(domain: string): Observable<DomainProcessingStatus> {
    return this.http.get<{ success: boolean; data: DomainProcessingStatus }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/processing-status`
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        } else {
          throw new Error('Failed to get processing status');
        }
      })
    );
  }

  /**
   * Trigger a processing step for a domain
   */
  runProcessingStep(domain: string, step: 'links-backfill' | 'links-classify' | 'topics-map' | 'topics-aggregate'): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/${step}`, {}
    );
  }

  /**
   * Run all processing steps sequentially for a domain
   */
  runAllProcessing(domain: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/process/run-all`, {}
    );
  }
}

